import { ENDPOINTS } from '../endpoints';
import {
  OrganizationDynamicPropertyDto,
  CreateOrganizationDynamicPropertyDto,
  UpdateOrganizationDynamicPropertyDto,
  OrganizationDynamicPropertyInsertDto,
} from '../types/dtos';
import { BaseService } from './base.service';
import { CustomResponseEntity } from '../types/common';
import { api } from '../client/axiosClient';

export class OrganizationDynamicPropertyService extends BaseService<
  OrganizationDynamicPropertyDto,
  CreateOrganizationDynamicPropertyDto,
  UpdateOrganizationDynamicPropertyDto
> {
  constructor() {
    super(ENDPOINTS.organizationDynamicProperties.base());
  }

  // Get dynamic properties by organization ID
  async getDynamicPropertiesByOrganization(
    organizationId: string
  ): Promise<CustomResponseEntity<OrganizationDynamicPropertyDto[]>> {
    return api.get(ENDPOINTS.organizationDynamicProperties.byOrganization(organizationId));
  }

  // Create dynamic property for an organization using the InsertDto
  async createDynamicPropertyWithInsertDto(
    data: OrganizationDynamicPropertyInsertDto
  ): Promise<CustomResponseEntity<OrganizationDynamicPropertyDto>> {
    return api.post(ENDPOINTS.organizationDynamicProperties.base(), data);
  }

  // Create multiple dynamic properties for an organization in batch
  async createMultipleDynamicProperties(
    properties: OrganizationDynamicPropertyInsertDto[]
  ): Promise<CustomResponseEntity<OrganizationDynamicPropertyDto[]>> {
    // Create all properties in parallel for better performance
    const promises = properties.map(property => 
      this.createDynamicPropertyWithInsertDto(property)
    );
    
    const results = await Promise.allSettled(promises);
    
    // Check if all operations were successful
    const successfulResults = results
      .filter((result): result is PromiseFulfilledResult<CustomResponseEntity<OrganizationDynamicPropertyDto>> => 
        result.status === 'fulfilled'
      )
      .map(result => result.value);
    
    const failedResults = results
      .filter((result): result is PromiseRejectedResult => 
        result.status === 'rejected'
      );
    
    // If there were any failures, continue with successful ones
    
    // Return the successful results
    return {
      data: successfulResults.map(result => result.data).filter(Boolean) as OrganizationDynamicPropertyDto[],
      status: successfulResults.length > 0 ? 'SUCCESS' : 'FAILED',
      code: successfulResults.length > 0 ? 200 : 400,
      httpErrorCode: failedResults.length > 0 ? 400 : 200,
      description: `Created ${successfulResults.length} dynamic properties successfully${failedResults.length > 0 ? `, ${failedResults.length} failed` : ''}`
    };
  }

  // Create dynamic property for an organization (legacy method for backward compatibility)
  async createDynamicProperty(
    organizationId: string,
    data: Omit<CreateOrganizationDynamicPropertyDto, 'organizationId'>
  ): Promise<CustomResponseEntity<OrganizationDynamicPropertyDto>> {
    const payload = {
      ...data,
      organizationId
    };
    return api.post(ENDPOINTS.organizationDynamicProperties.base(), payload);
  }

  // Update dynamic property
  async updateDynamicProperty(
    id: string,
    data: UpdateOrganizationDynamicPropertyDto
  ): Promise<CustomResponseEntity<OrganizationDynamicPropertyDto>> {
    return api.put(ENDPOINTS.organizationDynamicProperties.byId(id), data);
  }

  // Delete dynamic property
  async deleteDynamicProperty(id: string): Promise<CustomResponseEntity<void>> {
    return api.delete(ENDPOINTS.organizationDynamicProperties.byId(id));
  }
}

// Service instance
export const organizationDynamicPropertyService = new OrganizationDynamicPropertyService();
